<template>
  <div class="matrix-super-board mp-page dc-merged" :class="{ ready: loaded, embedded }">
    <header class="dc-chrome">
      <SectionHeader section-key="data" :desc="headerDesc">
        <template #actions>
          <el-button plain @click="openWorksPanel">账号作品</el-button>
          <el-button plain @click="openGroupManager">管理分组</el-button>
        </template>
      </SectionHeader>
    </header>

    <!-- 必须挂在滚动根下（不能塞进 header），才能真正钉住 -->
    <div class="dc-filter-bar">
      <div class="dc-filter-row dc-filter-row--primary">
        <div class="dc-filter-left">
          <strong class="dc-filter-title">筛选查看</strong>
          <el-date-picker
            :model-value="dateRangeDisplay"
            type="daterange"
            range-separator="至"
            start-placeholder="开始"
            end-placeholder="结束"
            value-format="YYYY-MM-DD"
            :clearable="false"
            :disabled-date="disabledDate"
            size="default"
            class="dc-date-picker"
            @update:model-value="onDateRangePick"
          />
          <div class="range-switch">
            <button type="button" :class="{ on: isPresetActive(7) }" @click="setDays(7)">近7日</button>
            <button type="button" :class="{ on: isPresetActive(14) }" @click="setDays(14)">近14日</button>
            <button type="button" :class="{ on: isPresetActive(30) }" @click="setDays(30)">近30日</button>
          </div>
        </div>
        <div class="dc-filter-right">
          <span class="dc-range-chip" :class="{ custom: isCustomRange }">{{ daysLabel }}</span>
          <el-button :loading="syncing" type="primary" size="small" @click="syncBoard">同步</el-button>
          <el-button :loading="loading && !syncing" size="small" @click="loadBoard(false)">本地</el-button>
        </div>
      </div>
      <div class="dc-filter-row dc-filter-row--secondary">
        <el-select
          v-model="viewPlat"
          clearable
          placeholder="全部平台"
          class="dc-filter-ctrl dc-filter-plat"
          @change="onViewPlatChange"
        >
          <el-option label="全部平台" value="" />
          <el-option v-for="p in platformOptions" :key="p.value" :label="p.label" :value="p.value" />
        </el-select>
        <el-select
          v-model="selectedGroupIds"
          multiple
          collapse-tags
          :max-collapse-tags="2"
          collapse-tags-tooltip
          clearable
          filterable
          teleported
          placeholder="多选分组，空=全展示"
          class="group-multi-select dc-filter-ctrl dc-filter-groups"
          @change="onGroupsChange"
        >
          <el-option
            v-for="g in boardGroups"
            :key="g.id"
            :label="`${g.name}（${platformHint(g)}）`"
            :value="Number(g.id)"
          />
        </el-select>
        <el-select
          v-model="contentType"
          clearable
          placeholder="全部类型"
          class="dc-filter-ctrl dc-filter-type"
          @change="onContentTypeChange"
        >
          <el-option label="全部类型" value="" />
          <el-option label="视频" value="video" />
          <el-option label="图文" value="normal" />
          <el-option label="文章" value="article" />
          <el-option label="问答" value="answer" />
        </el-select>
      </div>
      <p class="dc-filter-note">
        <template v-if="!selectedGroupIds.length">
          未选分组 · 全账号展示
        </template>
        <template v-else>
          已选 {{ selectedGroupIds.length }} 组 · 重叠账号去重
        </template>
        · 平台筛选只看所选范围内该平台数据
        <template v-if="isCustomRange">
          · 自定义区间按平台官方分日数据求和
        </template>
      </p>
    </div>

    <div v-if="contribRows.length" class="dc-tools">
      <span class="fv-label">平台看板</span>
      <div class="dc-tools-row">
        <button
          v-for="row in contribRows"
          :key="row.platform"
          type="button"
          class="plat-tool"
          :class="row.platform"
          @click="openPlatformGroup(row.platform)"
        >
          {{ row.label }}分组
          <em>{{ memberCountOf(row.platform) }}</em>
        </button>
        <button type="button" class="plat-tool works" @click="openWorksPanel()">
          账号作品列表
        </button>
      </div>
    </div>

    <el-alert v-if="error" type="error" :closable="false" show-icon :title="error" class="board-alert" />
    <el-alert
      v-for="(e, i) in softErrors"
      :key="i"
      type="warning"
      :closable="false"
      show-icon
      :title="`${platformName(e.platform)}：${e.error}`"
      class="board-alert"
    />

    <!-- ① 总览：KPI + 分平台 -->
    <section
      id="dc-overview"
      class="mp-panel panel overview-panel"
      v-loading="loading && !loaded"
    >
      <div class="section-head">
        <h2>矩阵总览 <small>创作中心指标软加总</small></h2>
        <p v-if="mergedNotes.views" class="scope-note">{{ mergedNotes.views }}</p>
      </div>
      <div class="hero-kpis hero-kpis--full">
        <div class="hk"><span>观看/播放/阅读</span><strong>{{ formatNum(scopedTotals.views) }}</strong></div>
        <div class="hk"><span>曝光</span><strong>{{ formatNum(scopedTotals.expose) }}</strong></div>
        <div class="hk"><span>互动</span><strong>{{ formatNum(scopedTotals.engage) }}</strong></div>
        <div class="hk up"><span>涨粉</span><strong>{{ formatNum(scopedTotals.fans_delta) }}</strong></div>
        <div class="hk"><span>总粉丝</span><strong>{{ formatNum(scopedTotals.followers) }}</strong></div>
        <div class="hk"><span>发布</span><strong>{{ formatNum(scopedTotals.publish) }}</strong></div>
      </div>
      <div v-if="customCoverageLag.length" class="range-caveat">
        <p v-for="c in customCoverageLag" :key="`lag-${c.label}`">
          {{ c.label }}：官方分日数据更新至 {{ c.last }}，更晚的日期平台尚未出数。
        </p>
      </div>
      <div v-if="Number(scopedTotals.publish) > 0" class="avg-strip">
        <div class="avg-item"><span>篇均观看</span><strong>{{ formatNum(avgMetrics.views) }}</strong></div>
        <div class="avg-item"><span>篇均曝光</span><strong>{{ formatNum(avgMetrics.expose) }}</strong></div>
        <div class="avg-item"><span>篇均互动</span><strong>{{ formatNum(avgMetrics.engage) }}</strong></div>
        <div class="avg-item"><span>篇均涨粉</span><strong>{{ formatNum(avgMetrics.fans_delta) }}</strong></div>
      </div>

      <div v-if="contribRows.length" class="contrib-block">
        <div class="section-head sub">
          <h3>分平台贡献 <small>点击筛选当前平台数据 · 详情进平台分组</small></h3>
        </div>
        <div class="contrib-grid">
          <div
            v-for="row in contribRows"
            :key="row.platform"
            class="contrib-card"
            :class="[row.platform, { on: viewPlat === row.platform, dim: viewPlat && viewPlat !== row.platform }]"
          >
            <button type="button" class="cc-main" @click="togglePlatFocus(row.platform)">
              <div class="cc-head">
                <strong>{{ row.label }}</strong>
                <span>{{ memberCountOf(row.platform) }} 号</span>
              </div>
              <div class="cc-kpis">
                <div><em>观看</em><b>{{ formatNum(row.views) }}</b></div>
                <div><em>曝光</em><b>{{ formatNum(row.expose) }}</b></div>
                <div><em>互动</em><b>{{ formatNum(row.engage) }}</b></div>
                <div><em>涨粉</em><b>{{ formatNum(row.fans_delta) }}</b></div>
                <div><em>粉丝</em><b>{{ formatNum(row.followers) }}</b></div>
                <div><em>发布</em><b>{{ formatNum(row.publish) }}</b></div>
              </div>
            </button>
            <button type="button" class="cc-drill" @click="openPlatformGroup(row.platform)">
              平台详情 →
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- ② 趋势 + 占比/篇均（原数据总览分析范围，口径仍用看板） -->
    <section id="dc-trend" class="mp-panel panel metric-panel">
      <div class="section-head">
        <h2>
          数据趋势
          <small v-if="filterScopeHint">· {{ filterScopeHint }}</small>
        </h2>
      </div>
      <div class="metric-tabs">
        <button
          v-for="tab in metricTabs"
          :key="tab.key"
          :class="{ on: metricTab === tab.key }"
          @click="onMetricTab(tab.key)"
        >{{ tab.label }}</button>
      </div>
      <div class="kpi-row">
        <button
          v-for="k in currentKpis"
          :key="k.key"
          class="kpi-card"
          :class="{ on: activeKpi === k.key }"
          @click="activeKpi = k.key"
        >
          <span>{{ k.label }}</span>
          <strong>{{ formatNum(k.value) }}</strong>
        </button>
      </div>
      <SimpleChart
        type="line"
        :height="280"
        :labels="trendLabels"
        :series="[{ name: activeKpiLabel, data: trendValues, color: '#326cf4', area: true }]"
      />
      <p class="merge-note">{{ activeKpiNote }}</p>

      <div class="analysis-grid">
        <div class="analysis-card">
          <div class="section-head sub"><h3>观看占比</h3></div>
          <SimpleChart
            v-if="viewsPie.length"
            type="pie"
            :height="260"
            :series="viewsPie"
          />
          <el-empty v-else description="暂无数据" :image-size="48" />
        </div>
        <div class="analysis-card">
          <div class="section-head sub"><h3>曝光占比</h3></div>
          <SimpleChart
            v-if="exposePie.length"
            type="pie"
            :height="260"
            :series="exposePie"
          />
          <el-empty v-else description="暂无数据" :image-size="48" />
        </div>
      </div>

      <div class="section-head sub" style="margin-top: 14px">
        <h3>分平台表现 <small>总量与篇均 · 创作中心口径</small></h3>
      </div>
      <el-table :data="platformPerfRows" size="small" class="data-table">
        <el-table-column prop="label" label="平台" width="100" />
        <el-table-column prop="publish" label="发布" width="72" :formatter="cellNum" />
        <el-table-column prop="views" label="总观看" width="96" :formatter="cellNum" />
        <el-table-column prop="expose" label="总曝光" width="96" :formatter="cellNum" />
        <el-table-column prop="engage" label="总互动" width="96" :formatter="cellNum" />
        <el-table-column prop="avg_views" label="篇均观看" width="96" :formatter="cellNum" />
        <el-table-column prop="avg_expose" label="篇均曝光" width="96" :formatter="cellNum" />
        <el-table-column prop="avg_engage" label="篇均互动" width="96" :formatter="cellNum" />
        <el-table-column prop="fans_delta" label="涨粉" width="80" :formatter="cellNum" />
        <el-table-column label="操作" width="100" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="openPlatformGroup(row.platform)">平台看板</el-button>
          </template>
        </el-table-column>
      </el-table>
    </section>

    <!-- ③ 账号 -->
    <section id="dc-accounts" class="mp-panel panel">
      <div class="section-head">
        <h2>
          分账号对比 <small>{{ filteredMembers.length }}/{{ mergedMembers.length }}</small>
          <small v-if="isCustomRange" class="warn-note">
            · 账号明细为平台官方 {{ fetchDaysLabel }} 窗口口径，不随自定义区间拆分
          </small>
        </h2>
        <div class="filter-bar">
          <el-input v-model="memberQuery" size="small" clearable placeholder="搜账号/UID" style="width: 160px" />
          <el-select v-model="memberStatus" size="small" clearable placeholder="状态" style="width: 120px">
            <el-option label="全部状态" value="" />
            <el-option label="正常" value="ok" />
            <el-option label="失败" value="fail" />
            <el-option label="注销/归档" value="dereg" />
            <el-option label="登录失效" value="expired" />
            <el-option label="快照" value="cache" />
          </el-select>
          <el-select v-model="memberSort" size="small" style="width: 140px">
            <el-option v-for="opt in memberSortOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
        </div>
      </div>
      <el-table
        :data="filteredMembers"
        size="small"
        class="data-table dc-scroll-table"
        @row-click="openMember"
      >
        <el-table-column label="平台" width="88" fixed>
          <template #default="{ row }">
            <span class="plat-tag" :class="row.platform">{{ row.platform_label }}</span>
          </template>
        </el-table-column>
        <el-table-column label="账号" min-width="180" fixed>
          <template #default="{ row }">
            <div class="acc-cell">
              <el-avatar :size="28" :src="row.avatar_url">{{ (row.display_name || '?')[0] }}</el-avatar>
              <div>
                <div>{{ row.display_name || row.account_id }}</div>
                <div class="sub mono">{{ row.external_user_id || row.account_id }}</div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="140">
          <template #default="{ row }">
            <div class="status-stack">
              <el-tag v-if="isDeregMember(row)" size="small" type="warning">
                {{ row.lifecycle_status === 'archived' ? '归档' : '注销' }}
              </el-tag>
              <el-tag v-else-if="isExpiredMember(row)" size="small" type="warning">失效</el-tag>
              <el-tag v-else-if="row.ok" size="small" type="success">正常</el-tag>
              <el-tag v-else size="small" type="danger">失败</el-tag>
              <el-tag v-if="row.from_cache || row.stub" size="small" type="info">快照</el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="followers" label="粉丝" width="88" :formatter="cellNum" />
        <el-table-column prop="views" label="观看" width="96" :formatter="cellNum" />
        <el-table-column prop="expose" label="曝光" width="96" :formatter="cellNum" />
        <el-table-column prop="likes" label="点赞" width="80" :formatter="cellNum" />
        <el-table-column prop="comments" label="评论" width="72" :formatter="cellNum" />
        <el-table-column prop="fans_delta" label="涨粉" width="72" :formatter="cellNum" />
        <el-table-column label="操作" width="120" fixed="right">
          <template #default="{ row }">
            <el-button
              v-if="row.ok || row.from_cache"
              link
              type="primary"
              @click.stop="openMember(row)"
            >账号看板</el-button>
            <span v-else class="sub">{{ row.error || '无快照' }}</span>
          </template>
        </el-table-column>
      </el-table>
    </section>

    <!-- ④ TOP + 作品明细（原账号作品/总览 TOP，数据来自看板合并内容） -->
    <section id="dc-top" class="mp-panel panel">
      <div class="section-head">
        <h2>观看 TOP <small>创作中心合并内容</small></h2>
      </div>
      <el-table
        :data="topWorks"
        size="small"
        class="data-table dc-scroll-table"
        max-height="360"
        @row-click="goContent"
      >
        <el-table-column type="index" label="#" width="48" fixed />
        <el-table-column label="平台" width="88" fixed>
          <template #default="{ row }">
            <span class="plat-tag" :class="row.platform">{{ row.platform_label }}</span>
          </template>
        </el-table-column>
        <el-table-column label="内容" min-width="220" fixed>
          <template #default="{ row }">
            <div class="note-title">{{ row.title || '(无标题)' }}</div>
            <div class="sub">{{ row.account_name || row.account_id }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="views" label="观看" width="88" :formatter="cellNum" />
        <el-table-column prop="expose" label="曝光" width="88" :formatter="cellNum" />
        <el-table-column prop="likes" label="点赞" width="72" :formatter="cellNum" />
        <el-table-column prop="engage" label="互动" width="72" :formatter="cellNum" />
        <el-table-column label="操作" width="72" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click.stop="goContent(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-empty v-if="!topWorks.length && loaded" description="暂无作品明细，可先同步或打开平台看板" :image-size="56" />
    </section>

    <section id="dc-works" class="mp-panel panel">
      <div class="section-head">
        <h2>
          作品明细
          <small>
            {{ contentPlatHint }} · 按发布时间
            {{ (dateRangeDisplay && dateRangeDisplay[0]) || '—' }}
            ~
            {{ (dateRangeDisplay && dateRangeDisplay[1]) || '—' }}
            · {{ filteredContent.length }} 篇
          </small>
        </h2>
        <div class="filter-bar">
          <el-input v-model="contentQuery" size="small" clearable placeholder="搜标题/账号" style="width: 180px" />
          <el-select v-model="contentSortField" size="small" style="width: 160px" title="再点当前项可切换正/倒序">
            <el-option
              v-for="opt in contentSortFields"
              :key="opt.value"
              :label="contentSortLabel(opt)"
              :value="opt.value"
            >
              <span class="sort-opt" @click="pickContentSort(opt.value)">{{ contentSortLabel(opt) }}</span>
            </el-option>
          </el-select>
          <el-button size="small" plain @click="openWorksPanel()">导出 / 预览工具</el-button>
        </div>
      </div>
      <div class="dc-content-desktop">
        <el-table
          :data="pagedContent"
          size="small"
          class="data-table dc-scroll-table"
          @row-click="goContent"
        >
          <el-table-column label="平台" width="88" fixed>
            <template #default="{ row }">
              <span class="plat-tag" :class="row.platform">{{ row.platform_label }}</span>
            </template>
          </el-table-column>
          <el-table-column label="内容" min-width="260" fixed>
            <template #default="{ row }">
              <div class="note-cell">
                <img v-if="row.cover_url" :src="row.cover_url" alt="" />
                <div>
                  <div class="note-title">{{ row.title || '(无标题)' }}</div>
                  <div class="sub">{{ row.account_name || row.account_id }} · {{ formatBeijingDateTime(row.published_at) }}</div>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="expose" label="曝光" width="88" :formatter="cellNum" />
          <el-table-column prop="views" label="观看" width="88" :formatter="cellNum" />
          <el-table-column prop="likes" label="点赞" width="72" :formatter="cellNum" />
          <el-table-column prop="comments" label="评论" width="72" :formatter="cellNum" />
          <el-table-column prop="collects" label="收藏" width="72" :formatter="cellNum" />
          <el-table-column prop="shares" label="分享" width="72" :formatter="cellNum" />
          <el-table-column prop="rise_fans" label="涨粉" width="72" :formatter="cellNum" />
          <el-table-column label="操作" width="72" fixed="right">
            <template #default="{ row }">
              <el-button link type="primary" @click.stop="goContent(row)">详情</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="dc-content-mobile">
        <button
          v-for="(row, idx) in pagedContent"
          :key="`${row.platform}-${row.item_id || row.account_id}-${idx}`"
          type="button"
          class="dc-work-card"
          @click="goContent(row)"
        >
          <img v-if="row.cover_url" class="dc-work-cover" :src="row.cover_url" alt="" />
          <div class="dc-work-body">
            <div class="dc-work-top">
              <span class="plat-tag" :class="row.platform">{{ row.platform_label }}</span>
              <span class="dc-work-time">{{ formatBeijingDateTime(row.published_at) }}</span>
            </div>
            <div class="note-title">{{ row.title || '(无标题)' }}</div>
            <div class="sub">{{ row.account_name || row.account_id }}</div>
            <div class="dc-work-metrics">
              <span><em>观看</em><b>{{ formatNum(row.views) }}</b></span>
              <span><em>曝光</em><b>{{ formatNum(row.expose) }}</b></span>
              <span><em>互动</em><b>{{ formatNum(row.engage || ((row.likes || 0) + (row.comments || 0) + (row.collects || 0) + (row.shares || 0))) }}</b></span>
            </div>
          </div>
          <span class="dc-work-go">详情</span>
        </button>
        <el-empty v-if="!pagedContent.length && loaded" description="暂无作品明细" :image-size="48" />
      </div>
      <div v-if="filteredContent.length > pageSize" class="pager">
        <el-pagination
          v-model:current-page="page"
          v-model:page-size="pageSize"
          :total="filteredContent.length"
          :page-sizes="[20, 50, 100, 200, 500]"
          layout="total, sizes, prev, pager, next"
          background
          small
        />
      </div>
      <p class="merge-note">
        本列表含「发布时间」落在所选日期内的<strong>全部</strong>作品（不截断）；上方 KPI 是该窗口内的流量合计，含更早发布作品在窗内产生的阅读/曝光。
      </p>
    </section>

    <el-empty
      v-if="loaded && !merged && !xhs && !douyin && !zhihu && !toutiao && !baijiahao"
      description="该分组暂无可用平台数据"
    />

    <AccountGroupManager
      v-model="groupManagerVisible"
      :accounts="publishAccounts"
      @changed="onGroupsChanged"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { publishV2Api } from '../api'
import SimpleChart from '../components/SimpleChart.vue'
import AccountGroupManager from '../components/AccountGroupManager.vue'
import SectionHeader from '../components/shell/SectionHeader.vue'
import { inheritBoardQuery, itemBoardLink, accountBoardLink } from '../utils/boardNav'
import { formatBeijingDateTime } from '../utils/dateTime'

const props = defineProps({
  /** 内嵌于 /data-analytics 时传入（兼容单分组） */
  groupIdProp: { type: [String, Number], default: '' },
  embedded: { type: Boolean, default: false },
})
const emit = defineEmits(['update:groupId'])

const route = useRoute()
const router = useRouter()
const embedded = computed(() => !!props.embedded)

const PLAT_COLORS = {
  xiaohongshu: '#FF2442',
  douyin: '#3B82F6',
  zhihu: '#1772F6',
  toutiao: '#F04142',
  baijiahao: '#2254F4',
}

const parseGroupIdsFromRoute = () => {
  const rawGroups = String(route.query.groups || '').trim()
  if (rawGroups) {
    return [
      ...new Set(
        rawGroups
          .split(',')
          .map((x) => Number(String(x).trim()))
          .filter((n) => Number.isFinite(n) && n > 0),
      ),
    ]
  }
  const single = Number(
    props.groupIdProp || route.params.groupId || route.query.group || 0,
  )
  return single > 0 ? [single] : []
}

/** 多选分组；空数组 = 全展示 */
const selectedGroupIds = ref(parseGroupIdsFromRoute())
/** 平台分组页跳转用：优先已选首个 */
const groupId = computed(() =>
  selectedGroupIds.value.length ? String(selectedGroupIds.value[0]) : '',
)
const days = ref(Number(route.query.range || 7))
const customStart = ref(String(route.query.start || '').trim())
const customEnd = ref(String(route.query.end || '').trim())
const loading = ref(false)
const syncing = ref(false)
const loaded = ref(false)
const error = ref('')
const payload = ref({})
const groups = ref([])
const publishAccounts = ref([])
const groupManagerVisible = ref(false)

const fmtDate = (d) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
const addDays = (base, n) => {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate())
  d.setDate(d.getDate() + n)
  return d
}
const parseDay = (s) => {
  const d = new Date(String(s || '').replace(/-/g, '/'))
  return Number.isNaN(d.getTime()) ? null : d
}
const daySpan = (start, end) => {
  const a = parseDay(start)
  const b = parseDay(end)
  if (!a || !b) return 1
  return Math.max(1, Math.round((b - a) / 86400000) + 1)
}
const rangeForDays = (n) => {
  const end = new Date()
  const start = addDays(end, -(Math.max(1, n) - 1))
  return [fmtDate(start), fmtDate(end)]
}
const coveringFetchDays = (start, end) => {
  const today = fmtDate(new Date())
  const need = Math.max(daySpan(start, end), daySpan(start, today))
  if (need <= 7) return 7
  if (need <= 14) return 14
  return 30
}
const normalizeDay = (d) => {
  const s = String(d || '').trim()
  if (s.length >= 10 && s[4] === '-') return s.slice(0, 10)
  return s.slice(0, 10)
}
const sliceSeries = (series, start, end) =>
  (series || []).filter((p) => {
    const d = normalizeDay(p?.date)
    return d && d >= start && d <= end
  })
const sumSeries = (series) =>
  (series || []).reduce((acc, p) => acc + (Number(p?.value) || 0), 0)
const sliceTrendMap = (trends, start, end) => {
  const out = {}
  for (const [k, series] of Object.entries(trends || {})) {
    out[k] = sliceSeries(series, start, end)
  }
  return out
}
const memberQuery = ref('')
const memberPlat = ref('')
const memberStatus = ref('')
const memberSort = ref('views_desc')
const contentPlat = ref('')
const contentQuery = ref('')
const contentSortField = ref('views')
const contentSortDir = ref('desc')
const contentSort = computed(() => `${contentSortField.value}_${contentSortDir.value}`)
const contentType = ref(String(route.query.type || '').trim())
const viewPlat = ref(String(route.query.plat || '').trim())
const page = ref(1)
const pageSize = ref(50)
const metricTab = ref('view')
const activeKpi = ref('views')

/** 小红书官方 note_type：0全部 / 1图文 / 2视频 */
const noteTypeParam = computed(() => {
  if (contentType.value === 'video') return '2'
  if (contentType.value === 'normal') return '1'
  return '0'
})
const typeLabel = computed(() => {
  const m = { video: '视频', normal: '图文', article: '文章', answer: '问答' }
  return m[contentType.value] || ''
})

const metricTabs = [
  { key: 'view', label: '观看数据' },
  { key: 'engage', label: '互动数据' },
  { key: 'fans', label: '涨粉数据' },
  { key: 'publish', label: '发布数据' },
]
const kpiDefs = {
  view: [
    { key: 'views', label: '观看/播放/阅读', noteKey: 'views' },
    { key: 'expose', label: '曝光', noteKey: 'expose' },
    { key: 'home_views', label: '主页访客', noteKey: 'home_views' },
  ],
  engage: [
    { key: 'engage', label: '互动合计', noteKey: 'engage' },
    { key: 'likes', label: '点赞', noteKey: 'likes' },
    { key: 'comments', label: '评论', noteKey: 'comments' },
    { key: 'collects', label: '收藏', noteKey: 'collects' },
    { key: 'shares', label: '分享', noteKey: 'shares' },
  ],
  fans: [
    { key: 'fans_delta', label: '涨粉', noteKey: 'fans_delta' },
    { key: 'followers', label: '总粉丝', noteKey: 'followers' },
  ],
  publish: [
    { key: 'publish', label: '发布/投稿', noteKey: 'publish' },
  ],
}
const memberSortOptions = [
  { value: 'views_desc', label: '观看↓', prop: 'views', order: 'desc' },
  { value: 'views_asc', label: '观看↑', prop: 'views', order: 'asc' },
  { value: 'expose_desc', label: '曝光↓', prop: 'expose', order: 'desc' },
  { value: 'likes_desc', label: '点赞↓', prop: 'likes', order: 'desc' },
  { value: 'followers_desc', label: '粉丝↓', prop: 'followers', order: 'desc' },
  { value: 'fans_delta_desc', label: '涨粉↓', prop: 'fans_delta', order: 'desc' },
]
const contentSortFields = [
  { value: 'views', label: '观看', prop: 'views' },
  { value: 'expose', label: '曝光', prop: 'expose' },
  { value: 'likes', label: '点赞', prop: 'likes' },
  { value: 'engage', label: '互动', prop: 'engage' },
  { value: 'rise_fans', label: '涨粉', prop: 'rise_fans' },
  { value: 'published', label: '发布时间', prop: 'published_at' },
]
const contentSortOptions = contentSortFields.flatMap((f) => [
  { ...f, value: `${f.value}_desc`, order: 'desc' },
  { ...f, value: `${f.value}_asc`, order: 'asc' },
])

const sortArrow = (field, dir) => {
  if (field === 'published') return dir === 'desc' ? '最新→最早' : '最早→最新'
  return dir === 'desc' ? '↓' : '↑'
}
const contentSortLabel = (opt) =>
  opt.value === contentSortField.value
    ? `${opt.label} ${sortArrow(opt.value, contentSortDir.value)}`
    : opt.label
/** 再点当前项即反向；点别的项换指标并回到降序 */
const pickContentSort = (field) => {
  if (field === contentSortField.value) {
    contentSortDir.value = contentSortDir.value === 'desc' ? 'asc' : 'desc'
    return
  }
  contentSortField.value = field
  contentSortDir.value = 'desc'
}

const group = computed(() => payload.value.group || {})
const headerDesc = computed(() => {
  const scope = filterScopeHint.value || `${platformCount.value} 平台 · ${memberTotal.value} 账号`
  return `${scopeLabel.value} · ${scope}`
})

const scopeLabel = computed(() => {
  if (!selectedGroupIds.value.length) return '全矩阵'
  return group.value.name || `${selectedGroupIds.value.length} 个分组`
})
const merged = computed(() => payload.value.merged || null)
const t = computed(() => merged.value?.totals || {})
const viewPlatLabel = computed(() => {
  if (!viewPlat.value) return ''
  return merged.value?.platform_labels?.[viewPlat.value] || platformName(viewPlat.value)
})
const filterScopeHint = computed(() => {
  const parts = []
  if (viewPlatLabel.value) parts.push(viewPlatLabel.value)
  if (typeLabel.value) parts.push(typeLabel.value)
  if (viewPlat.value && !typeLabel.value) {
    parts.push(`${memberCountOf(viewPlat.value)} 个账号`)
  } else if (typeLabel.value) {
    parts.push(`${scopedContentPool.value.length} 篇内容`)
  }
  return parts.join(' · ')
})

const contentTypeOf = (row) => {
  const raw = String(
    row?.media_type || row?.content_type || row?.note_type || row?.type || '',
  ).toLowerCase()
  if (!raw) {
    if (row?.platform === 'zhihu') return 'article'
    if (row?.platform === 'toutiao' || row?.platform === 'baijiahao') return 'article'
    return ''
  }
  if (raw === '2' || raw.includes('video') || raw === '视频') return 'video'
  if (raw === '1' || raw.includes('normal') || raw === '图文' || raw === 'image') return 'normal'
  if (raw.includes('article') || raw === '文章') return 'article'
  if (raw.includes('answer') || raw.includes('问答') || raw === 'question') return 'answer'
  return raw
}

const isCustomRange = computed(() => !!(customStart.value && customEnd.value))
const fetchDaysLabel = computed(() => `近${days.value}日`)
const daysLabel = computed(() => {
  if (isCustomRange.value) return `${customStart.value} ~ ${customEnd.value}`
  if (days.value >= 30) return '近30日'
  if (days.value >= 14) return '近14日'
  return '近7日'
})
const dateRangeDisplay = computed(() => {
  if (isCustomRange.value) return [customStart.value, customEnd.value]
  return rangeForDays(Number(days.value) || 7)
})
const isPresetActive = (n) => {
  if (isCustomRange.value) {
    const [s, e] = rangeForDays(n)
    return customStart.value === s && customEnd.value === e
  }
  return Number(days.value) === n
}
const disabledDate = (date) => {
  const today = new Date()
  today.setHours(23, 59, 59, 999)
  const min = addDays(new Date(), -29)
  min.setHours(0, 0, 0, 0)
  return date.getTime() > today.getTime() || date.getTime() < min.getTime()
}

/**
 * 数据查看口径（完整场景）
 * 1) 快捷窗 7/14/30：后端拉各平台原生窗并裁到请求窗 → by_platform / trends
 * 2) 自定义日期：前端按分日趋势切片；缺字段用「区间内有发布时间的作品」补
 * 3) 平台筛选：只影响总览加总/趋势/账号/作品列表；分平台卡仍展示全平台便于对比
 * 4) 类型筛选：video/normal 会带 note_type 重拉小红书；其余类型按作品池过滤
 * 5) TOP/作品明细：发布时间落在当前日期范围内（无发布时间不计入）
 * 6) 粉丝：始终用最新存量，不做区间加总
 */
const inDateWindow = (publishedAt) => {
  const s = String(publishedAt || '').trim()
  const [start, end] = dateRangeDisplay.value || []
  if (!start || !end) return true
  // 无发布时间：不能判断是否落在窗内，不计入日期相关汇总/TOP
  if (!s) return false
  const day = s.slice(0, 10)
  return day >= start && day <= end
}

/** 日期+类型内容池（不含平台筛选，供分平台贡献兜底） */
const datedTypeContentPool = computed(() => {
  const typ = contentType.value
  return mergedContentItems.value.filter((row) => {
    if (typ && contentTypeOf(row) !== typ) return false
    if (!inDateWindow(row.published_day || row.published_at || row.publish_time)) return false
    return true
  })
})

/** 再叠加平台筛选：供 TOP / 作品明细 / 当前平台内容汇总 */
const scopedContentPool = computed(() => {
  const plat = viewPlat.value
  if (!plat) return datedTypeContentPool.value
  return datedTypeContentPool.value.filter((row) => row.platform === plat)
})

/** 原始趋势（未按自定义日期切片） */
const rawScopedTrends = computed(() => {
  if (contentType.value) {
    if (viewPlat.value === 'xiaohongshu' && (contentType.value === 'video' || contentType.value === 'normal')) {
      return merged.value?.by_platform_trends?.xiaohongshu || {}
    }
    return {}
  }
  const plat = viewPlat.value
  if (!plat) return merged.value?.trends || {}
  return merged.value?.by_platform_trends?.[plat] || merged.value?.trends || {}
})

/**
 * 趋势一律裁到当前筛选区间：后端会把永久日表的全部历史并进 trends，
 * 快捷窗若不裁，近 7 日也会画出上百天的曲线。
 */
const scopedTrends = computed(() => {
  const raw = rawScopedTrends.value
  const [start, end] = dateRangeDisplay.value || []
  if (!start || !end) return raw
  return sliceTrendMap(raw, start, end)
})

/** 内容池按平台汇总（不吃平台筛选，避免点选单平台后其它卡兜底被掏空） */
const contentKpisByPlatform = computed(() => {
  const map = {}
  for (const row of datedTypeContentPool.value) {
    const p = row.platform
    if (!p) continue
    if (!map[p]) {
      map[p] = {
        views: 0,
        expose: 0,
        likes: 0,
        comments: 0,
        collects: 0,
        shares: 0,
        engage: 0,
        fans_delta: 0,
        publish: 0,
      }
    }
    const slot = map[p]
    slot.views += Number(row.views) || 0
    slot.expose += Number(row.expose) || 0
    slot.likes += Number(row.likes) || 0
    slot.comments += Number(row.comments) || 0
    slot.collects += Number(row.collects) || 0
    slot.shares += Number(row.shares) || 0
    slot.engage += Number(row.engage) || 0
    slot.fans_delta += Number(row.rise_fans) || 0
    slot.publish += 1
  }
  return map
})

/** 平台各指标是否有官方分日序列，决定能否按自定义日期精确拆分 */
const trendSupport = computed(() => merged.value?.trend_support || {})
const trendCoverage = computed(() => merged.value?.trend_coverage || {})

/**
 * 自定义区间只认官方分日序列的区间和（含 0，那是真实的 0）。
 * 平台不提供该指标的分日数据时返回 null＝不可拆分，绝不用作品 lifetime 值顶替。
 */
const windowedMetric = (sliced, key, support, contentValue) => {
  if (support?.[key]) {
    const series = sliced?.[key]
    // 分日序列为空＝该区间没有被覆盖，回退作品 lifetime 估算，绝不显示 0
    if (Array.isArray(series) && series.length > 0) return sumSeries(series)
  }
  return Number(contentValue) || 0
}

/** 后端未下发 trend_support 时，按原始序列是否非空推断，避免整屏「—」 */
const supportOf = (plat, rawTrends) => {
  const fromApi = trendSupport.value?.[plat]
  if (fromApi) return fromApi
  const inferred = {}
  for (const [k, series] of Object.entries(rawTrends || {})) {
    inferred[k] = Array.isArray(series) && series.length > 0
  }
  return inferred
}

/** 自定义区间末端超出平台分日数据覆盖时的提示 */
const customCoverageLag = computed(() => {
  if (!isCustomRange.value) return []
  const labels = merged.value?.platform_labels || {}
  const platTrends = merged.value?.by_platform_trends || {}
  const out = []
  for (const row of visibleContribRows.value) {
    let last = trendCoverage.value?.[row.platform]?.last
    if (!last) {
      for (const series of Object.values(platTrends[row.platform] || {})) {
        for (const p of series || []) {
          const d = normalizeDay(p?.date)
          if (d && (!last || d > last)) last = d
        }
      }
    }
    if (last && last < customEnd.value) {
      out.push({ label: labels[row.platform] || platformName(row.platform), last })
    }
  }
  return out
})

const trendTypeHint = computed(() => {
  if (contentType.value) {
    if (viewPlat.value === 'xiaohongshu' && (contentType.value === 'video' || contentType.value === 'normal')) {
      return '小红书图文/视频：总览按官方题材窗口；作品列表按发布时间筛选'
    }
    return '类型筛选只改作品列表与发布篇数；观看/曝光/互动仍为全类型官方窗'
  }
  if (isCustomRange.value) {
    return '自定义区间按平台官方分日数据逐日求和'
  }
  return ''
})

const mergedNotes = computed(() => merged.value?.notes || {})
const mergedContent = computed(() => payload.value.merged_content || {})
const mergedContentItems = computed(() => mergedContent.value?.items || [])
const mergedMembers = computed(() => payload.value.merged_members?.items || [])
const softErrors = computed(() => payload.value.errors || [])
const xhs = computed(() => payload.value.xiaohongshu || null)
const zhihu = computed(() => payload.value.zhihu || null)
const douyin = computed(() => payload.value.douyin || null)
const toutiao = computed(() => payload.value.toutiao || null)
const baijiahao = computed(() => payload.value.baijiahao || null)

const platformCount = computed(() => (merged.value?.platforms || []).length)
const memberTotal = computed(() => mergedMembers.value.length)
const platformOptions = computed(() =>
  (merged.value?.platforms || []).map((p) => ({
    value: p,
    label: merged.value?.platform_labels?.[p] || platformName(p),
  })),
)
const contribRows = computed(() => {
  const by = merged.value?.by_platform || {}
  const labels = merged.value?.platform_labels || {}
  const plats = merged.value?.platforms || Object.keys(by)
  const fromContent = contentKpisByPlatform.value
  const platTrends = merged.value?.by_platform_trends || {}
  const xhsNoteFiltered =
    noteTypeParam.value === '1' || noteTypeParam.value === '2'

  return plats.map((p) => {
    const base = by[p] || {}
    const content = fromContent[p] || {}
    const stock = Number(base.followers) || 0
    const contentPublish = Number(content.publish) || 0

    // 小红书图文/视频：后端已按 note_type 重拉官方窗，直接用看板 KPI
    if (contentType.value && p === 'xiaohongshu' && xhsNoteFiltered) {
      const likes = Number(base.likes) || 0
      const comments = Number(base.comments) || 0
      const collects = Number(base.collects) || 0
      const shares = Number(base.shares) || 0
      return {
        platform: p,
        label: labels[p] || platformName(p),
        views: Number(base.views) || 0,
        expose: Number(base.expose) || 0,
        likes,
        comments,
        collects,
        shares,
        engage: Number(base.engage) || likes + comments + collects + shares,
        fans_delta: Number(base.fans_delta) || 0,
        followers: stock,
        publish: Number(base.publish) || contentPublish,
        home_views: Number(base.home_views) || 0,
      }
    }

    // 其它类型筛选：流量仍用官方窗/分日拆分；作品 lifetime 绝不当窗口 KPI。
    // 无该类型作品的平台：发布=0，流量不串用（显示空卡）。
    if (contentType.value && contentPublish <= 0) {
      return {
        platform: p,
        label: labels[p] || platformName(p),
        views: 0,
        expose: 0,
        likes: 0,
        comments: 0,
        collects: 0,
        shares: 0,
        engage: 0,
        fans_delta: 0,
        followers: stock,
        publish: 0,
        home_views: 0,
      }
    }

    // 自定义日期：只按官方分日序列求和；平台没有分日数据的指标返回 null＝不可拆分
    if (isCustomRange.value) {
      const raw = platTrends[p] || {}
      const sliced = sliceTrendMap(raw, customStart.value, customEnd.value)
      const support = supportOf(p, raw)
      const m = (key, fallback) => windowedMetric(sliced, key, support, fallback)
      const likes = m('likes', content.likes)
      const comments = m('comments', content.comments)
      const collects = m('collects', content.collects)
      const shares = m('shares', content.shares)
      const engage = support.engage && (sliced.engage || []).length > 0
        ? sumSeries(sliced.engage)
        : Number(content.engage) || likes + comments + collects + shares
      return {
        platform: p,
        label: labels[p] || platformName(p),
        views: m('views', content.views),
        expose: m('expose', content.expose),
        likes,
        comments,
        collects,
        shares,
        engage,
        fans_delta: m('fans_delta', content.fans_delta),
        followers: stock,
        publish: contentType.value ? contentPublish : m('publish', content.publish),
        home_views: m('home_views', 0),
      }
    }

    // 快捷窗：只信任后端官方窗口 KPI（包括 0）；作品指标是 lifetime，不能兜底窗口值。
    const likes = Number(base.likes) || 0
    const comments = Number(base.comments) || 0
    const collects = Number(base.collects) || 0
    const shares = Number(base.shares) || 0
    return {
      platform: p,
      label: labels[p] || platformName(p),
      views: Number(base.views) || 0,
      expose: Number(base.expose) || 0,
      likes,
      comments,
      collects,
      shares,
      engage: Number(base.engage) || likes + comments + collects + shares,
      fans_delta: Number(base.fans_delta) || 0,
      followers: stock,
      publish: contentType.value ? contentPublish : Number(base.publish) || 0,
      home_views: Number(base.home_views) || 0,
    }
  })
})

/** 平台筛选后的分平台行：总览/占比/分平台表现同源 */
const visibleContribRows = computed(() => {
  const plat = viewPlat.value
  if (!plat) return contribRows.value
  return contribRows.value.filter((r) => r.platform === plat)
})

/** 总览 KPI 与分平台贡献同源加总，避免两套账 */
const SUM_KEYS = [
  'views',
  'expose',
  'likes',
  'comments',
  'collects',
  'shares',
  'engage',
  'fans_delta',
  'publish',
  'home_views',
]
const scopedTotals = computed(() => {
  const rows = visibleContribRows.value
  const out = {}
  for (const key of SUM_KEYS) {
    // 全部平台都无法拆分该指标时不编数：保持 null，界面显示「—」
    const vals = rows.map((r) => r[key]).filter((v) => v !== null && v !== undefined)
    out[key] = vals.length ? vals.reduce((a, b) => a + (Number(b) || 0), 0) : null
  }
  const plat = viewPlat.value
  if (plat) {
    out.followers = Number(merged.value?.by_platform?.[plat]?.followers) || 0
  } else {
    out.followers = Number(t.value.followers) || 0
  }
  return out
})
const avgMetrics = computed(() => {
  const pubs = Number(scopedTotals.value.publish) || 0
  const safe = (n) => (n === null ? null : pubs > 0 ? (Number(n) || 0) / pubs : 0)
  return {
    views: safe(scopedTotals.value.views),
    expose: safe(scopedTotals.value.expose),
    engage: safe(scopedTotals.value.engage),
    fans_delta: safe(scopedTotals.value.fans_delta),
  }
})
const viewsPie = computed(() =>
  visibleContribRows.value
    .map((r) => ({
      name: r.label,
      value: Number(r.views) || 0,
      color: PLAT_COLORS[r.platform],
    }))
    .filter((x) => x.value > 0),
)
const exposePie = computed(() =>
  visibleContribRows.value
    .map((r) => ({
      name: r.label,
      value: Number(r.expose) || 0,
      color: PLAT_COLORS[r.platform],
    }))
    .filter((x) => x.value > 0),
)
const platformPerfRows = computed(() =>
  visibleContribRows.value.map((r) => {
    const pubs = Number(r.publish) || 0
    const avg = (n) => (n === null ? null : pubs > 0 ? (Number(n) || 0) / pubs : 0)
    return {
      ...r,
      avg_views: avg(r.views),
      avg_expose: avg(r.expose),
      avg_engage: avg(r.engage),
    }
  }),
)
const topWorks = computed(() =>
  sortByOption(scopedContentPool.value, contentSortOptions, 'views_desc').slice(0, 10),
)
const contentPlatHint = computed(() => {
  const by = mergedContent.value?.by_platform || {}
  const parts = []
  if (by.xiaohongshu) parts.push(`小红书 ${by.xiaohongshu}`)
  if (by.douyin) parts.push(`抖音 ${by.douyin}`)
  if (by.zhihu) parts.push(`知乎 ${by.zhihu}`)
  if (by.toutiao) parts.push(`头条 ${by.toutiao}`)
  if (by.baijiahao) parts.push(`百家号 ${by.baijiahao}`)
  return parts.join(' · ') || '软合并'
})

const currentKpis = computed(() =>
  (kpiDefs[metricTab.value] || []).map((d) => {
    const raw = scopedTotals.value[d.key]
    return { ...d, value: raw === null ? null : Number(raw || 0) }
  }),
)
const activeKpiLabel = computed(
  () => currentKpis.value.find((k) => k.key === activeKpi.value)?.label || '趋势',
)
const activeKpiNote = computed(() => {
  const hit = currentKpis.value.find((k) => k.key === activeKpi.value)
  const base = mergedNotes.value[hit?.noteKey || activeKpi.value] || mergedNotes.value.views || ''
  const head = [viewPlatLabel.value, typeLabel.value].filter(Boolean).join(' · ')
  if (trendTypeHint.value) return trendTypeHint.value
  if (head) return `${head} · ${base}`
  return base
})
const trendLabels = computed(() =>
  (scopedTrends.value?.[activeKpi.value] || []).map((p) => {
    const d = String(p.date || '')
    return d.length >= 10 ? d.slice(5) : d
  }),
)
const trendValues = computed(() =>
  (scopedTrends.value?.[activeKpi.value] || []).map((p) => Number(p.value) || 0),
)

const sortByOption = (list, options, sortValue) => {
  const opt = options.find((o) => o.value === sortValue) || options[0]
  const prop = opt?.prop
  const dir = opt?.order === 'asc' ? 1 : -1
  return [...list].sort((a, b) => {
    if (prop === 'published_at') {
      return String(a?.[prop] || '').localeCompare(String(b?.[prop] || ''), 'zh') * dir
    }
    const av = Number(a?.[prop] ?? 0) || 0
    const bv = Number(b?.[prop] ?? 0) || 0
    if (av === bv) {
      return String(b?.display_name || b?.title || '').localeCompare(
        String(a?.display_name || a?.title || ''),
        'zh',
      )
    }
    return (av - bv) * dir
  })
}

const typeAccountKeys = computed(() => {
  if (!contentType.value) return null
  const set = new Set()
  for (const row of scopedContentPool.value) {
    const uid = String(row.external_user_id || row.account_id || '').trim()
    if (uid) set.add(`${row.platform}:${uid}`)
    if (row.account_id) set.add(`${row.platform}:${row.account_id}`)
  }
  return set
})

function memberHasTypePublish(row) {
  const keys = typeAccountKeys.value
  if (!keys) return true
  const uid = String(row.external_user_id || row.account_id || '').trim()
  const aid = String(row.account_id || '').trim()
  return (
    (uid && keys.has(`${row.platform}:${uid}`)) ||
    (aid && keys.has(`${row.platform}:${aid}`))
  )
}

function isDeregMember(row) {
  const lc = String(row?.lifecycle_status || '').toLowerCase()
  if (lc === 'deregistered' || lc === 'archived') return true
  const name = String(row?.display_name || '')
  return /注销|已注销/.test(name)
}

function isExpiredMember(row) {
  return String(row?.lifecycle_status || '').toLowerCase() === 'expired'
}

const filteredMembers = computed(() => {
  const q = String(memberQuery.value || '').trim().toLowerCase()
  const plat = viewPlat.value || memberPlat.value
  let list = mergedMembers.value.filter((row) => {
    if (plat && row.platform !== plat) return false
    if (
      memberStatus.value === 'ok' &&
      (!row.ok || isDeregMember(row) || isExpiredMember(row))
    ) {
      return false
    }
    if (
      memberStatus.value === 'fail' &&
      (row.ok || isDeregMember(row) || isExpiredMember(row))
    ) {
      return false
    }
    if (memberStatus.value === 'dereg' && !isDeregMember(row)) return false
    if (memberStatus.value === 'expired' && !isExpiredMember(row)) return false
    if (memberStatus.value === 'cache' && !(row.from_cache || row.stub)) return false
    // 类型筛选只作用于作品列表，账号表保持完整，避免窗外发布账号被藏掉
    if (!q) return true
    const hay = `${row.display_name || ''} ${row.account_id || ''} ${row.external_user_id || ''}`.toLowerCase()
    return hay.includes(q)
  })
  return sortByOption(list, memberSortOptions, memberSort.value)
})

const filteredContent = computed(() => {
  const q = String(contentQuery.value || '').trim().toLowerCase()
  let list = scopedContentPool.value
  if (q) {
    list = list.filter((row) => {
      const hay = `${row.title || ''} ${row.account_name || ''} ${row.account_id || ''}`.toLowerCase()
      return hay.includes(q)
    })
  }
  return sortByOption(list, contentSortOptions, contentSort.value)
})
const pagedContent = computed(() => {
  const start = (page.value - 1) * pageSize.value
  return filteredContent.value.slice(start, start + pageSize.value)
})

const boardGroups = computed(() =>
  (groups.value || []).filter((g) =>
    (g.accounts || []).some((a) =>
      ['xiaohongshu', 'zhihu', 'douyin', 'toutiao', 'baijiahao'].includes(a.platform),
    ),
  ),
)

const formatNum = (v) => {
  if (v === null) return '—'
  const n = Number(v || 0)
  if (!Number.isFinite(n)) return '0'
  if (Math.abs(n) >= 10000) return `${(n / 10000).toFixed(1)}万`
  return String(Math.round(n))
}
const cellNum = (_r, _c, v) => formatNum(v)
const platformName = (p) =>
  ({ xiaohongshu: '小红书', zhihu: '知乎', douyin: '抖音', toutiao: '头条', baijiahao: '百家号' }[p] || p)
const platformHint = (g) => {
  const set = new Set((g.accounts || []).map((a) => a.platform))
  return [...set].map(platformName).filter(Boolean).join('+') || '—'
}
const memberCountOf = (plat) =>
  Number(payload.value.merged_members?.by_platform?.[plat] || 0)

function onMetricTab(key) {
  metricTab.value = key
  activeKpi.value = (kpiDefs[key] || [])[0]?.key || 'views'
}

async function reloadGroups() {
  const data = await publishV2Api.listAccountGroups()
  groups.value = data?.items || data || []
}

    async function loadBoard(sync = false) {
  if (sync) syncing.value = true
  else loading.value = true
  error.value = ''
  try {
    const [pubStart, pubEnd] = dateRangeDisplay.value || []
    const data = await publishV2Api.getMatrixScopeDatacenterBoard({
      group_ids: selectedGroupIds.value.length
        ? selectedGroupIds.value.join(',')
        : '',
      days: days.value,
      note_type: noteTypeParam.value,
      sync,
      sync_all_windows: sync ? true : undefined,
      publish_start: pubStart || undefined,
      publish_end: pubEnd || undefined,
    })
    payload.value = data || {}
    loaded.value = true
    page.value = 1
  } catch (e) {
    error.value = e?.response?.data?.detail || e?.message || '加载失败'
  } finally {
    loading.value = false
    syncing.value = false
  }
}

function syncFilterQuery(extra = {}) {
  const query = { ...route.query, range: String(days.value), ...extra }
  if (viewPlat.value) query.plat = viewPlat.value
  else delete query.plat
  if (contentType.value) query.type = contentType.value
  else delete query.type
  if (customStart.value && customEnd.value) {
    query.start = customStart.value
    query.end = customEnd.value
  } else {
    delete query.start
    delete query.end
  }
  if (selectedGroupIds.value.length) {
    query.groups = selectedGroupIds.value.join(',')
    // 兼容旧单参
    query.group = String(selectedGroupIds.value[0])
  } else {
    delete query.groups
    delete query.group
  }
  delete query.panel
  if (embedded.value) {
    router.replace({ path: '/data-analytics', query })
  } else {
    router.replace({ query })
  }
}

function openPlatformGroup(platform) {
  if (platform === 'xiaohongshu') openLegacyXhs()
  else if (platform === 'douyin') openDouyinGroup()
  else if (platform === 'zhihu') openZhihuGroup()
  else if (platform === 'toutiao') openToutiaoGroup()
  else if (platform === 'baijiahao') openBaijiahaoGroup()
}

/** 账号作品：二级页；分组管理：本页弹窗，不跳转 */
function openWorksPanel() {
  const query = { panel: 'works' }
  if (selectedGroupIds.value.length) {
    query.groups = selectedGroupIds.value.join(',')
    query.group = String(selectedGroupIds.value[0])
  }
  router.push({ path: '/data-analytics', query })
}

async function ensurePublishAccounts() {
  if (publishAccounts.value.length) return
  try {
    const res = await publishV2Api.listAccounts()
    publishAccounts.value = res?.data?.items || res?.items || []
  } catch {
    publishAccounts.value = []
  }
}

async function openGroupManager() {
  await ensurePublishAccounts()
  groupManagerVisible.value = true
}

async function onGroupsChanged() {
  groupManagerVisible.value = false
  await reloadGroups()
  const ids = new Set(boardGroups.value.map((g) => Number(g.id)))
  const next = selectedGroupIds.value.filter((id) => ids.has(Number(id)))
  if (next.length !== selectedGroupIds.value.length) {
    selectedGroupIds.value = next
    syncFilterQuery()
    await loadBoard(false)
  }
}

function setDays(d) {
  customStart.value = ''
  customEnd.value = ''
  days.value = d
  syncFilterQuery({ range: String(d) })
  loadBoard(false)
}
function onDateRangePick(val) {
  if (!Array.isArray(val) || val.length < 2 || !val[0] || !val[1]) return
  let start = String(val[0]).slice(0, 10)
  let end = String(val[1]).slice(0, 10)
  if (!parseDay(start) || !parseDay(end)) return
  if (start > end) {
    const tmp = start
    start = end
    end = tmp
  }
  const min = fmtDate(addDays(new Date(), -29))
  const max = fmtDate(new Date())
  let clipped = false
  if (start < min) {
    start = min
    clipped = true
  }
  if (end > max) {
    end = max
    clipped = true
  }
  if (clipped) {
    ElMessage.info('自定义范围仅支持近30日内，已自动裁剪')
  }
  for (const n of [7, 14, 30]) {
    const [ps, pe] = rangeForDays(n)
    if (start === ps && end === pe) {
      setDays(n)
      return
    }
  }
  customStart.value = start
  customEnd.value = end
  const fetchD = coveringFetchDays(start, end)
  days.value = fetchD
  syncFilterQuery({ range: String(fetchD), start, end })
  // 发布日期窗变了必须重拉，保证作品明细按发布时间落窗
  loadBoard(false)
}
function onViewPlatChange(v) {
  viewPlat.value = v || ''
  memberPlat.value = viewPlat.value
  contentPlat.value = viewPlat.value
  syncFilterQuery()
  page.value = 1
}
function togglePlatFocus(plat) {
  viewPlat.value = viewPlat.value === plat ? '' : plat
  onViewPlatChange(viewPlat.value)
}
function onContentTypeChange(v) {
  contentType.value = v || ''
  syncFilterQuery()
  // 图文/视频会驱动小红书 note_type 重拉；文章/问答仅前端内容汇总
  if (!v || v === 'video' || v === 'normal') {
    loadBoard(false)
  } else {
    page.value = 1
  }
}
function syncBoard() {
  loadBoard(true)
}
function onGroupsChange(ids) {
  selectedGroupIds.value = [...new Set((ids || []).map(Number).filter((n) => n > 0))]
  try {
    if (selectedGroupIds.value.length) {
      localStorage.setItem('mp_datacenter_group_ids', selectedGroupIds.value.join(','))
      localStorage.setItem('mp_datacenter_group_id', String(selectedGroupIds.value[0]))
    } else {
      localStorage.removeItem('mp_datacenter_group_ids')
    }
  } catch { /* ignore */ }
  emit('update:groupId', selectedGroupIds.value[0] || '')
  syncFilterQuery()
  loadBoard(false)
}

function navGroupId() {
  return groupId.value || String(boardGroups.value[0]?.id || '')
}
function openLegacyXhs() {
  const gid = navGroupId()
  if (!gid) return
  router.push({
    path: `/data-analytics/xhs/group/${gid}`,
    query: { range: String(days.value >= 30 ? 30 : 7) },
  })
}
function openDouyinGroup() {
  const gid = navGroupId()
  if (!gid) return
  const range = days.value >= 30 ? 30 : days.value === 1 ? 1 : 7
  router.push({
    path: `/data-analytics/douyin/group/${gid}`,
    query: { range: String(range) },
  })
}
function openZhihuGroup() {
  const gid = navGroupId()
  if (!gid) return
  const range = [0, 7, 14, 30].includes(days.value) ? days.value : 7
  router.push({
    path: `/data-analytics/zhihu/group/${gid}`,
    query: { range: String(range) },
  })
}
function openToutiaoGroup() {
  const gid = navGroupId()
  if (!gid) return
  const range = days.value >= 30 ? 30 : days.value >= 14 ? 14 : 7
  router.push({
    path: `/data-analytics/toutiao/group/${gid}`,
    query: { range: String(range) },
  })
}
function openBaijiahaoGroup() {
  const gid = navGroupId()
  if (!gid) return
  router.push({
    path: `/data-analytics/baijiahao/group/${gid}`,
    query: { range: String(days.value >= 30 ? 30 : 7) },
  })
}
function groupCtx(range) {
  const gid = navGroupId()
  return inheritBoardQuery({
    group: gid,
    fromGroup: gid,
    groupName: group.value.name || scopeLabel.value || '',
    range: String(range),
  })
}
function openMember(row) {
  if (!row?.ok) return
  const id = row.external_user_id || row.account_id
  if (!id) return
  if (row.platform === 'xiaohongshu') {
    router.push(accountBoardLink('xhs', id, groupCtx(days.value >= 30 ? 30 : 7)))
  } else if (row.platform === 'douyin') {
    router.push(accountBoardLink('douyin', id, groupCtx(days.value >= 30 ? 30 : 7)))
  } else if (row.platform === 'zhihu') {
    router.push(accountBoardLink('zhihu', id, groupCtx(days.value)))
  } else if (row.platform === 'toutiao') {
    const range = days.value >= 30 ? 30 : days.value >= 14 ? 14 : 7
    router.push(accountBoardLink('toutiao', id, groupCtx(range)))
  } else if (row.platform === 'baijiahao') {
    router.push(accountBoardLink('baijiahao', id, groupCtx(days.value >= 30 ? 30 : 7)))
  }
}
function goContent(row) {
  if (!row?.item_id) return
  const uid = row.external_user_id || row.account_id
  if (!uid) return
  if (row.platform === 'douyin') {
    router.push(itemBoardLink('douyin', uid, row.item_id, groupCtx(days.value >= 30 ? 30 : 7)))
    return
  }
  if (row.platform === 'xiaohongshu') {
    router.push(itemBoardLink('xhs', uid, row.item_id, groupCtx(days.value >= 30 ? 30 : 7)))
  }
}

watch(
  () => [route.query.groups, route.query.group, route.params.groupId],
  () => {
    const next = parseGroupIdsFromRoute()
    const cur = selectedGroupIds.value.join(',')
    if (next.join(',') === cur) return
    selectedGroupIds.value = next
    viewPlat.value = String(route.query.plat || '').trim()
    contentType.value = String(route.query.type || '').trim()
    memberPlat.value = viewPlat.value
    contentPlat.value = viewPlat.value
    customStart.value = String(route.query.start || '').trim()
    customEnd.value = String(route.query.end || '').trim()
    const d = Number(route.query.range || days.value)
    if ([7, 14, 30].includes(d)) days.value = d
    else if (customStart.value && customEnd.value) {
      days.value = coveringFetchDays(customStart.value, customEnd.value)
    }
    loadBoard(false)
  },
)
watch([viewPlat, contentPlat, contentQuery, contentSort, contentType], () => {
  page.value = 1
})

onMounted(async () => {
  await reloadGroups()
  // 无 URL 分组时尝试恢复本地多选；仍为空则全展示
  if (!selectedGroupIds.value.length) {
    try {
      const remembered = String(localStorage.getItem('mp_datacenter_group_ids') || '')
        .split(',')
        .map((x) => Number(x))
        .filter((n) => n > 0)
      const valid = new Set(boardGroups.value.map((g) => Number(g.id)))
      const kept = remembered.filter((id) => valid.has(id))
      // 默认全展示：不自动回填历史单选
      if (kept.length > 1) selectedGroupIds.value = kept
    } catch { /* ignore */ }
  }
  syncFilterQuery()
  await loadBoard(false)
})
</script>

<style scoped>
.matrix-super-board {
  color: var(--mp-text);
}

/* 数据中心吃满主栏，不受全局 .mp-page 1460 限制 */
.matrix-super-board.dc-merged,
.matrix-super-board.embedded {
  max-width: none;
  width: 100%;
}

.dc-chrome {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.dc-chrome > .mp-section-head {
  margin-bottom: 0;
}

/* 钉在 .main 滚动容器顶部；勿再包进会滚出视口的父级 */
.dc-filter-bar {
  position: sticky;
  top: -12px; /* 对齐 Layout .main--dense 的 padding-top */
  z-index: 50;
  margin: 0 -6px;
  padding: 10px 12px 8px;
  background: #fff;
  border: 1px solid var(--mp-border);
  border-radius: 0 0 var(--mp-radius) var(--mp-radius);
  box-shadow: 0 10px 24px rgba(30, 48, 82, 0.08);
}

.dc-filter-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
}

.dc-filter-row--primary {
  justify-content: space-between;
}

.dc-filter-row--secondary {
  margin-top: 8px;
}

.dc-filter-left {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.dc-filter-title {
  font-size: 13px;
  font-weight: 800;
  color: var(--mp-text);
  margin-right: 2px;
  flex-shrink: 0;
}

.dc-date-picker {
  width: 260px;
  max-width: 100%;
}

.dc-filter-ctrl {
  width: 120px;
}

.dc-filter-groups {
  min-width: 200px;
  width: min(280px, 100%);
  flex: 1 1 200px;
}

.dc-filter-type {
  width: 110px;
}

/* 多选分组：标签可见，避免看起来像单选文本 */
.group-multi-select :deep(.el-select__wrapper) {
  min-height: 32px;
}

.group-multi-select :deep(.el-tag) {
  max-width: 120px;
}

.dc-filter-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.dc-range-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--mp-accent-soft);
  color: var(--mp-accent);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.dc-range-chip.custom {
  background: var(--mp-warning-soft);
  color: #c46a2b;
}

.warn-note {
  color: #c46a2b;
}

.range-caveat {
  margin: 10px 0 0;
  padding: 8px 12px;
  border-radius: 8px;
  background: var(--mp-warning-soft);
  color: #8a5620;
  font-size: 12px;
  line-height: 1.6;
}

.range-caveat p {
  margin: 0;
}

.dc-filter-note {
  margin: 6px 0 0;
  font-size: 11px;
  color: var(--mp-text-muted);
  line-height: 1.4;
}





.dc-tools {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.dc-tools-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.plat-tool {
  border: 1px solid var(--mp-border);
  background: var(--mp-surface);
  color: var(--mp-text-secondary);
  border-radius: var(--mp-radius-sm);
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 650;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: var(--mp-shadow-sm);
}

.plat-tool em {
  font-style: normal;
  color: var(--mp-text-muted);
  font-weight: 500;
}

.plat-tool:hover {
  border-color: var(--mp-border-strong);
  background: var(--mp-accent-soft);
  color: var(--mp-accent);
}

.plat-tool.works {
  border-style: dashed;
}

.overview-panel .scope-note {
  margin: 0;
  font-size: 12px;
  color: var(--mp-text-muted);
  max-width: 520px;
  line-height: 1.45;
}

.hero-kpis--full {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px;
  margin-top: 4px;
}

.contrib-block {
  margin-top: 18px;
}

.section-head.sub {
  margin-bottom: 10px;
}

.section-head.sub h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--mp-text);
}

.section-head.sub small {
  margin-left: 8px;
  color: var(--mp-text-muted);
  font-weight: 400;
}

.contrib-card {
  display: flex;
  flex-direction: column;
  text-align: left;
  border: 1px solid var(--mp-border);
  background: var(--mp-surface-muted);
  border-radius: var(--mp-radius);
  padding: 0;
  overflow: hidden;
  color: inherit;
}

.cc-main {
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  padding: 12px;
  cursor: pointer;
  font: inherit;
}

.cc-drill {
  border: 0;
  border-top: 1px solid var(--mp-border);
  background: var(--mp-surface);
  color: var(--mp-accent);
  padding: 8px 12px;
  text-align: left;
  cursor: pointer;
  font-size: 12px;
}

.cc-drill:hover {
  background: var(--mp-accent-soft);
}

.range-switch {
  display: inline-flex;
  background: #f3f5f8;
  border-radius: 8px;
  padding: 3px;
}

.range-switch button {
  border: 0;
  background: transparent;
  color: var(--mp-text-muted);
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 650;
}

.range-switch button.on {
  background: var(--mp-surface);
  color: var(--mp-accent);
  box-shadow: var(--mp-shadow-sm);
}

.board-alert {
  margin-top: 0;
}

.hero-kpis {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

@media (max-width: 1100px) {
  .hero-kpis--full {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .hero-kpis--full {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.hk {
  padding: 12px 14px;
  border-radius: var(--mp-radius);
  background: var(--mp-surface-muted);
  border: 1px solid var(--mp-border);
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.hk span {
  font-size: 12px;
  color: var(--mp-text-muted);
}

.hk strong {
  font-family: var(--mp-font-mono);
  font-size: 22px;
  color: var(--mp-text);
  letter-spacing: -0.03em;
}

.hk.up strong {
  color: var(--mp-success);
}

.avg-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;
}

.avg-item {
  padding: 10px 12px;
  border-radius: var(--mp-radius-sm);
  background: var(--mp-surface);
  border: 1px dashed var(--mp-border-strong);
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.avg-item span {
  font-size: 11px;
  color: var(--mp-text-muted);
}

.avg-item strong {
  font-family: var(--mp-font-mono);
  font-size: 16px;
  color: var(--mp-text);
}

.analysis-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 16px;
}

.analysis-card {
  padding: 10px 12px;
  border-radius: var(--mp-radius);
  background: var(--mp-surface-muted);
  border: 1px solid var(--mp-border);
}

@media (max-width: 900px) {
  .analysis-grid,
  .avg-strip {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 560px) {
  .analysis-grid,
  .avg-strip {
    grid-template-columns: 1fr;
  }
}

.filter-view {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  padding: 12px 14px;
  border-radius: var(--mp-radius-lg);
  background: var(--mp-surface);
  border: 1px solid var(--mp-border);
  box-shadow: var(--mp-shadow-sm);
}

.fv-label {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  color: var(--mp-text-secondary);
}

.fv-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  flex: 1;
}

.fv-quick {
  margin: 0;
}

.panel {
  padding: 18px 20px 20px;
}

.section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  gap: 12px;
  flex-wrap: wrap;
}

.section-head h2 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--mp-text);
}

.section-head small {
  margin-left: 8px;
  font-size: 12px;
  color: var(--mp-text-muted);
  font-weight: 400;
}

.filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}

/* 撑满整行，点选项任意位置都能命中反向切换 */
.sort-opt {
  display: block;
  width: 100%;
}

.contrib-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px;
}

.contrib-card.dim {
  opacity: 0.45;
}
.contrib-card.on {
  border-color: var(--mp-accent);
  background: var(--mp-accent-soft);
}

.cc-head {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.cc-head strong {
  font-size: 14px;
  color: var(--mp-text);
}

.cc-head span {
  font-size: 12px;
  color: var(--mp-text-muted);
}

.cc-kpis {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.cc-kpis em {
  display: block;
  font-style: normal;
  font-size: 11px;
  color: var(--mp-text-muted);
}

.cc-kpis b {
  font-family: var(--mp-font-mono);
  font-size: 15px;
  font-weight: 600;
  color: var(--mp-text);
}

.status-stack {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}
.acc-cell,
.note-cell {
  display: flex;
  gap: 8px;
  align-items: center;
}

.note-cell img {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  background: var(--mp-surface-muted);
}

.note-title {
  font-size: 13px;
  color: var(--mp-text);
}

.sub {
  font-size: 11px;
  color: var(--mp-text-muted);
}

.mono {
  font-family: var(--mp-font-mono);
  font-size: 11px;
}

.plat-tag {
  display: inline-block;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 6px;
  background: var(--mp-surface-muted);
  color: var(--mp-text-secondary);
  border: 1px solid var(--mp-border);
}

.plat-tag.xiaohongshu {
  background: #ffe8ec;
  color: #d91a3d;
  border-color: transparent;
}

.plat-tag.douyin {
  background: #eef2ff;
  color: #4338ca;
  border-color: transparent;
}

.plat-tag.zhihu {
  background: #e8f1ff;
  color: #1762c4;
  border-color: transparent;
}

.plat-tag.toutiao {
  background: #fff1e8;
  color: #c2410c;
  border-color: transparent;
}

.plat-tag.baijiahao {
  background: #e8f8f1;
  color: #0f8a5f;
  border-color: transparent;
}

.data-table {
  width: 100%;
  cursor: pointer;
}

.dc-scroll-table {
  width: 100%;
}

.dc-scroll-table :deep(.el-table__header th) {
  white-space: nowrap;
}

.dc-content-mobile {
  display: none;
}

.dc-work-card {
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: start;
  width: 100%;
  border: 1px solid var(--mp-border);
  background: var(--mp-surface);
  border-radius: var(--mp-radius);
  padding: 10px;
  text-align: left;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.dc-work-card + .dc-work-card {
  margin-top: 8px;
}

.dc-work-card:active {
  background: var(--mp-surface-muted);
}

.dc-work-cover {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  object-fit: cover;
  background: var(--mp-surface-muted);
}

.dc-work-body {
  min-width: 0;
}

.dc-work-top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.dc-work-time {
  font-size: 11px;
  color: var(--mp-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dc-work-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  margin-top: 8px;
}

.dc-work-metrics span {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.dc-work-metrics em {
  font-style: normal;
  font-size: 11px;
  color: var(--mp-text-muted);
}

.dc-work-metrics b {
  font-family: var(--mp-font-mono);
  font-size: 14px;
  font-weight: 700;
}

.dc-work-go {
  align-self: center;
  color: var(--mp-accent);
  font-size: 12px;
  font-weight: 650;
  white-space: nowrap;
  padding-top: 18px;
}

.metric-tabs {
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.metric-tabs button {
  border: 0;
  background: #f3f5f8;
  color: var(--mp-text-muted);
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 650;
}

.metric-tabs button.on {
  background: var(--mp-accent-soft);
  color: var(--mp-accent);
}

.kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}

.kpi-card {
  text-align: left;
  border: 1px solid var(--mp-border);
  background: var(--mp-surface-muted);
  border-radius: var(--mp-radius);
  padding: 10px 12px;
  color: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.kpi-card.on {
  border-color: var(--mp-accent);
  background: var(--mp-accent-soft);
}

.kpi-card span {
  font-size: 12px;
  color: var(--mp-text-muted);
}

.kpi-card strong {
  font-family: var(--mp-font-mono);
  font-size: 20px;
  color: var(--mp-text);
}

.merge-note {
  margin: 10px 0 0;
  font-size: 12px;
  color: var(--mp-text-muted);
  line-height: 1.5;
}

.pager {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

@media (max-width: 1100px) {
  .dc-date-picker {
    width: 240px;
  }
  .panel {
    padding-left: 14px;
    padding-right: 14px;
  }
}

@media (max-width: 960px) {
  .hero-kpis {
    grid-template-columns: repeat(2, 1fr);
  }

  .dc-filter-bar {
    top: -8px;
    margin: 0 -2px;
    padding: 8px 10px;
  }

  .dc-filter-row--primary .dc-filter-right {
    width: 100%;
    justify-content: flex-end;
  }

  .dc-date-picker {
    width: min(280px, 100%);
    flex: 1 1 200px;
  }

  .dc-content-desktop {
    display: none;
  }

  .dc-content-mobile {
    display: block;
  }


  .pager {
    justify-content: center;
  }

  .pager :deep(.el-pagination) {
    flex-wrap: wrap;
    justify-content: center;
  }
}

@media (max-width: 560px) {
  .dc-work-card {
    grid-template-columns: 48px minmax(0, 1fr);
  }

  .dc-work-cover {
    width: 48px;
    height: 48px;
  }

  .dc-work-go {
    display: none;
  }

}
</style>
